import { Link } from "react-router-dom";
import { ArrowRight, Radio, Briefcase, LayoutGrid, Crown, Newspaper, ShoppingBag, Car, Network, History, Sparkles } from "lucide-react";
import { PublicHeader, Footer } from "../components/PublicChrome";
import { DgcEmblem } from "../components/Logo";
import { WhatsOn } from "../components/public/WhatsOn";
import { Portfolio } from "../components/public/Portfolio";
import { FUTURE } from "../lib/heritage";
import { useApi } from "../lib/api";

export const Eyebrow = ({ children, light }) => <div className={`eyebrow mb-4 ${light ? "!text-[color:var(--gold-hi)]" : ""}`}>{children}</div>;

import { HeroVideo } from "../components/HeroVideo";
import { Linkify } from "../components/heritage/Linkify";

const Hero = () => (
  <section className="relative overflow-hidden bg-brand-deep" data-testid="legacy-hero">
    <HeroVideo id="CmS9-s4YmLo" title="Despite Automobile Museum" credit="Kpone TV · YouTube" creditUrl="https://youtube.com/shorts/CmS9-s4YmLo" />
    <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-16 grid lg:grid-cols-2 gap-10 items-center">
      <div className="rise">
        <Eyebrow light>Despite Group of Companies · Est. 1997</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] drop-shadow-[0_2px_12px_rgba(0,0,0,.35)]">
          From a cassette stall<br />to a <span className="text-[color:var(--gold-hi)]">Ghanaian institution.</span>
        </h1>
        <p className="mt-6 text-base md:text-lg text-white/90 max-w-xl leading-relaxed">
          Nearly three decades of building — radio, television, food, finance, manufacturing and heritage. This is the story, the structure and the future of DESPITE.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/history" className="btn-gold !bg-[color:var(--gold-hi)] !text-[#064D29] !border-white/40 hover:!bg-white" data-testid="hero-legacy-btn">Explore the legacy</Link>
          <Link to="/hub" className="btn-ghost" data-testid="hero-hub-btn"><Radio size={15} /> Live Hub</Link>
        </div>
        <div className="mt-12 grid grid-cols-4 gap-4 max-w-lg border-t border-white/20 pt-6">
          {[["1997", "DGC founded"], ["1999", "Peace FM on air"], ["14", "Entities"], ["5", "Broadcast brands"]].map(([v, l]) => (
            <div key={l}><div className="font-display text-2xl md:text-3xl font-bold text-[color:var(--gold-hi)]">{v}</div><div className="font-data text-[9px] uppercase tracking-wider text-white/70 mt-1">{l}</div></div>
          ))}
        </div>
      </div>
      <div className="flex justify-center rise" style={{ animationDelay: ".15s" }}>
        <div className="relative">
          <div className="absolute -inset-6 rounded-full border border-[color:var(--gold-hi)]/50" />
          <div className="absolute -inset-12 rounded-full border border-dashed border-[color:var(--green)]/25 animate-[spin_60s_linear_infinite] hidden md:block" />
          <DgcEmblem size={280} className="relative float-slow shadow-[0_30px_80px_-20px_rgba(0,0,0,.55)] hidden md:block" />
          <DgcEmblem size={150} className="relative float-slow shadow-[0_30px_80px_-20px_rgba(0,0,0,.55)] md:hidden" />
        </div>
      </div>
    </div>
  </section>
);

const LEADERS = [
  { slug: "osei-kwame-despite", name: "Dr. Osei Kwame Despite", role: "Founder & Chairman, Despite Group of Companies", photo: "/brand/dr-despite.jpg",
    text: "Born in Wiamoase, he began as a small trader in Dunkwa-On-Offin. The cassette trade became Despite Stores; music became media; media became a group — starting with Peace FM in 1999." },
  { slug: "ernest-ofori-sarpong", name: "Dr. Ernest Ofori Sarpong", role: "Co-founder & Partner · Founder, Special Group of Companies", photo: "/brand/dr-ofori-sarpong.png",
    text: "Born in Kumasi and trained in finance, he built the Special Group and co-founded UTV, U2 Salt and Best Point Savings & Loans with Dr. Despite — a partnership at the heart of DGC." },
];

export const Founder = () => (
  <section id="principals" className="bg-brand-deep text-white relative overflow-hidden" data-testid="founder-section">
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-20">
      <Eyebrow light>The Principals</Eyebrow>
      <h2 className="font-display text-3xl md:text-5xl font-bold max-w-3xl">Two visionaries. One partnership.</h2>
      <div className="grid md:grid-cols-2 gap-8 mt-12">
        {LEADERS.map((l) => (
          <div key={l.slug} className="bg-white/5 border border-[color:var(--gold-hi)]/50 rounded-md p-6 flex flex-col" data-testid={`principal-${l.slug}`}>
            <img src={l.photo} alt={l.name} className="w-full h-72 object-cover object-top rounded gold-frame" data-testid={`principal-photo-${l.slug}`} />
            <h3 className="font-display text-2xl md:text-3xl font-bold mt-6">{l.name}</h3>
            <p className="text-[color:var(--gold-hi)] text-sm mt-1">{l.role}</p>
            <p className="text-white/80 mt-4 leading-relaxed flex-1"><Linkify text={l.text} light /></p>
            <Link to={`/people/${l.slug}`} className="mt-6 self-start inline-flex items-center gap-2 bg-[color:var(--gold-hi)] text-[#064D29] font-bold text-sm px-4 py-2 rounded hover:bg-white transition-colors" data-testid={`principal-bio-link-${l.slug}`}>Read full biography <ArrowRight size={14} /></Link>
          </div>
        ))}
      </div>
      <p className="font-data text-[10px] uppercase tracking-wider text-white/50 mt-6">Sources: Wikipedia · GhanaWeb</p>
    </div>
  </section>
);

const Present = () => {
  const { data } = useApi("/public/news?limit=4");
  return (
    <section id="present" className="max-w-7xl mx-auto px-5 md:px-8 pb-16" data-testid="present-section">
      <Eyebrow>Live right now</Eyebrow>
      <div className="grid lg:grid-cols-2 gap-6 [&>*]:min-w-0">
        <WhatsOn />
        <div className="panel p-6 min-w-0">
          <div className="flex items-center justify-between mb-4"><span className="eyebrow">Latest from Peacefmonline.com</span><Link to="/hub" className="text-xs text-brand-green hover:underline" data-testid="present-hub-link">Open Live Hub →</Link></div>
          <div className="divide-y divide-[color:var(--line)]">
            {(data?.items || []).map((a) => (
              <a key={a.article_id} href={a.url} target="_blank" rel="noreferrer" className="flex gap-4 py-3 group" data-testid={`present-news-${a.article_id}`}>
                {a.photo && <img src={a.photo} alt="" className="w-20 h-14 object-cover rounded shrink-0" />}
                <span className="text-sm text-ink group-hover:text-brand-green transition-colors">{a.title}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const Future = () => (
  <section id="future" className="bg-[#F4F8F5] border-y border-[color:var(--line)]" data-testid="future-section">
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-24">
      <Eyebrow>The Future</Eyebrow>
      <h2 className="font-display text-3xl md:text-5xl font-bold text-ink max-w-3xl">The next chapter of DESPITE.</h2>
      <p className="text-sm text-ink-3 mt-3">Proposed vision prepared by the DGC WPA — for the Chairman's direction.</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        {FUTURE.map(([t, d], i) => (
          <div key={t} className="panel panel-hover p-6" data-testid={`future-pillar-${i}`}>
            <div className="font-display text-4xl text-[color:var(--gold)] font-bold">0{i + 1}</div>
            <div className="font-display text-lg text-ink font-bold mt-3">{t}</div>
            <p className="text-sm text-ink-3 mt-2">{d}</p>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-3 gap-4 mt-6">
        {[["30 days", "Recruitment portal live"], ["60 days", "Staff availability dashboard (Peace FM → UTV)"], ["90 days", "Real-time sentiment monitor with Twi validation"]].map(([d, t]) => (
          <div key={d} className="bg-brand-green text-white rounded-md p-5 border-b-4 border-[color:var(--gold-hi)]" data-testid={`future-roadmap-${d.split(" ")[0]}`}>
            <div className="font-data text-xs text-[color:var(--gold-hi)] uppercase tracking-widest">{d}</div><div className="font-display text-lg mt-1">{t}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const T = "/brand/tiles/";
const TILES = [
  ["/hub", Radio, "Live Radio & TV", "Peace, Okay, Neat, Hello & UTV — live now", `${T}live.webp`],
  ["/hub#news", Newspaper, "News & Sentiment", "Today's stories from Peacefmonline.com", `${T}news.jpg`],
  ["/shop", ShoppingBag, "Shop & Suppliers", "Order DGC products, partner with us", "/brand/products/neat-fufu-range.png"],
  ["/museum", Car, "Automobile Museum", "Plan a visit, book a tour", "/brand/museum/bugatti-type-35.webp"],
  ["/careers", Briefcase, "Careers", "Open roles, CV drop, talent pool", `${T}careers.jpg`],
  ["/organisation", Network, "The Group", "Six divisions, fourteen entities", "/brand/museum/opening-1.webp"],
  ["/history", History, "Our History", "From a cassette stall, 1997 → today", "/brand/illustrations/despite-stores.jpg"],
  ["/principals", Crown, "The Principals", "Dr. Despite & Dr. Ofori Sarpong", "/brand/dr-despite.jpg"],
  ["/future", Sparkles, "The Future", "The next chapter of DESPITE", `${T}future.jpg`],
  ["/login", LayoutGrid, "Staff Portal", "Ops, HR & Executive intelligence", `${T}portal.jpg`],
];

const Tiles = () => (
  <section className="max-w-7xl mx-auto px-5 md:px-8 py-10 md:py-14" data-testid="app-tiles">
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
      {TILES.map(([to, I, t, d, img], i) => (
        <Link key={to} to={to} className="group relative rounded-lg overflow-hidden p-4 md:p-5 flex flex-col min-h-[170px] md:min-h-[190px] text-white bg-[#0e0b04] shadow-[1px_1px_0_rgba(70,40,0,.85)] transition-transform hover:-translate-y-1 rise" style={{ animationDelay: `${i * 0.04}s` }} data-testid={`tile-${t.toLowerCase().replace(/\W+/g, "-")}`}>
          <img src={img} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <span className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/10" />
          <span className="relative w-9 h-9 rounded-full bg-white/15 backdrop-blur-md border border-white/25 flex items-center justify-center"><I size={16} className="text-white" /></span>
          <span className="relative font-display text-base md:text-lg font-bold mt-auto pt-4 flex items-center gap-1 leading-tight">{t} <ArrowRight size={14} className="text-[color:var(--gold-hi)] group-hover:translate-x-1 transition-transform shrink-0" /></span>
          <span className="relative text-[11px] md:text-xs mt-1 text-white/80">{d}</span>
        </Link>
      ))}
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="legacy-home">
      <PublicHeader />
      <Hero />
      <Tiles />
      <Portfolio />
      <Present />
      <Footer />
    </div>
  );
}
